import type {
  LiveBreakHost,
  LiveIngestCredentials,
  LiveSlotHost,
  LiveStream,
  LiveStreamSegment,
  LiveStreamViewers,
  LiveSyncRecording,
  Store,
} from "@hobbyshopify/backend-client";
import { Rx, RxList } from "subtree.js/core";
import { type FormStatus, idleForm, idleLoad, type LoadStatus } from "../../core/primitives/load-status";
import type { ShareTarget } from "../../core/primitives/share-links";
import type { StreamTotals } from "../../core/primitives/stream-cockpit";
import type { Wire } from "../../core/primitives/wire-stats";
import type { BroadcastPhase } from "../../core/services/broadcast-studio";

export type CockpitDialog = "end" | "encoder";
export type ChatTab = "chat" | "room";

export class LiveStreamState {
  constructor(readonly streamId: string) {}

  readonly status = new Rx<LoadStatus>(idleLoad);
  readonly stream = new Rx<LiveStream | null>(null);
  /** The line-up's boxes, in line-up order. */
  readonly breaks = new RxList<LiveBreakHost>();
  readonly segments = new RxList<LiveStreamSegment>();
  /** The caller hosts: drawing, finishing and ending are theirs. */
  readonly isHost = new Rx(true);
  readonly takeOver = new Rx<FormStatus>(idleForm);
  /** Terminal: no chat, no sales, no reveals, no stream. */
  readonly over = new Rx(false);
  readonly store = new Rx<Store | null>(null);
  /** The stream's page on hobbyshop.ai, where viewers watch and buy. */
  readonly share = new Rx<ShareTarget | null>(null);
  /** Read from the store the app already holds, so the cockpit can say so before a press rather than after one. */
  readonly paymentsReady = new Rx(false);

  /** On a phone the line-up and spots are a drawer over the picture. */
  readonly lineupOpen = new Rx(false);
  /** The right column shows the room as a buyer has it instead. */
  readonly preview = new Rx(false);
  readonly dialog = new Rx<CockpitDialog | null>(null);

  /** The break the host chose to look at; null follows the stream. */
  readonly pickedBreakId = new Rx<string | null>(null);
  readonly shownBreakId = new Rx<string | null>(null);
  readonly shownBreak = new Rx<LiveBreakHost | null>(null);
  readonly slotsStatus = new Rx<LoadStatus>(idleLoad);
  readonly slots = new RxList<LiveSlotHost>();
  /** Who is in the shown break, in spot order. */
  readonly buyers = new RxList<string>();

  /** People in the room right now, or null before presence has answered. */
  readonly watching = new Rx<number | null>(null);
  /** The chat column shows the conversation, or who is in the room (loaded while shown). */
  readonly chatTab = new Rx<ChatTab>("chat");
  readonly viewersStatus = new Rx<LoadStatus>(idleLoad);
  readonly viewers = new Rx<LiveStreamViewers | null>(null);
  readonly totals = new Rx<StreamTotals>({ sold: 0, slots: 0, gross: 0, cost: 0 });

  // The camera, mirrored from the studio. The picture itself renders in the stage above the router.
  readonly phase = new Rx<BroadcastPhase>("ready");
  readonly cameraError = new Rx<string | null>(null);
  readonly cameras = new RxList<MediaDeviceInfo>();
  readonly cameraId = new Rx("");
  readonly wire = new Rx<Wire | null>(null);
  readonly interrupted = new Rx(false);
  readonly reconnecting = new Rx(false);
  readonly onAir = new Rx(false);
  readonly hasMic = new Rx(false);
  readonly micMuted = new Rx(false);

  readonly end = new Rx<FormStatus>(idleForm);
  /** The refusal's code: `breaks_ripping` becomes a one-press "close it and end". */
  readonly endCode = new Rx<string | null>(null);
  readonly recording = new Rx<FormStatus>(idleForm);
  readonly recordingResult = new Rx<LiveSyncRecording | null>(null);
  readonly encoder = new Rx<FormStatus>(idleForm);
  readonly ingest = new Rx<LiveIngestCredentials | null>(null);
}

export abstract class LiveStreamActions {
  abstract toggleLineup(): void;
  abstract selectChatTab(tab: ChatTab): void;
  abstract togglePreview(): void;
  abstract openDialog(dialog: CockpitDialog | null): void;
  abstract pickBreak(breakId: string): void;
  abstract goLive(): void;
  abstract stopCamera(): void;
  abstract restartCamera(): void;
  abstract switchCamera(deviceId: string): void;
  abstract toggleMic(): void;
  abstract endStream(finishRipping?: boolean): Promise<void>;
  abstract checkRecording(): Promise<void>;
  abstract getStreamKey(): Promise<void>;
  /** Become the host, for a shift change. */
  abstract takeOver(): Promise<void>;
}
